import { Link } from 'react-router-dom';
import { Img } from '../Img/Img';
import { useFormatting, useTranslation } from '../../i18n/i18n';
import { Res, useApi } from '../../state/api';
import { ApiOutstandingItem } from '../../types';
import { renderTemplate } from '../utils';
import './OutstandingItems.css';

/**
 * What the customer has ordered and not yet been given: lines on backorder, and lines for articles
 * the warehouse does not stock and orders in per customer. The two are one list because the
 * customer's question is the same for both - where is my thing - and the badge answers why.
 *
 * Nothing renders when there is nothing outstanding, which is the ordinary case. An order the
 * warehouse has not had a business day to ship is not outstanding either - the endpoint leaves it
 * out, or an article we do not stock would read as a wait the moment the customer ordered it.
 */
export const OutstandingItems = (): JSX.Element | null => {
	const { data } = useApi(Res.outstandingItems, {});
	const t = useTranslation().myPages;
	const routes = useTranslation().routes;
	const orderT = useTranslation().orderPages;
	const { dateTimeFormat } = useFormatting();

	if (!data?.items?.length) return null;

	const stillMissing = (item: ApiOutstandingItem) => item.quantity - item.quantityFulfilled;

	return (
		<div className="outstanding-items">
			{data.items.map((item) => (
				<Link className="outstanding-item" key={item.id} to={`${routes.yourOrders}/${item.order.id}`}>
					<div className="outstanding-item-image">
						{item.image ? <Img src={item.image} width={50} height={50} alt={item.product.name} /> : null}
					</div>

					<div className="outstanding-item-row">
						<div className="outstanding-item-name">
							<div className="name">{item.product.name}</div>
							<div className="article">{item.product.displayId}</div>
						</div>

						<div className="outstanding-item-kind">
							<span className={`badge ${item.waitingFor}`}>
								{item.waitingFor === 'backorder' ? t.backordered : t.specialOrderItem}
							</span>
						</div>

						<div className="outstanding-item-quantity">
							{renderTemplate(t.awaitingDelivery, { count: stillMissing(item) })}
						</div>

						<div className="outstanding-item-order">
							<div>
								{orderT.orderNumber}: {item.order.displayId}
							</div>
							<div className="date">{dateTimeFormat(new Date(item.order.datePurchased))}</div>
						</div>
					</div>
				</Link>
			))}
		</div>
	);
};
